import type {
  ICreateCourseRequest,
  ICreateCourseResponse,
  IGetCourseForEditRequest,
  IGetCourseForEditResponse,
  IGetCourseJoinCodeRequest,
  IGetCourseJoinCodeResponse,
  IGetCourseRequest,
  IGetCourseResponse,
  IGetCoursesRequest,
  IGetCoursesResponse,
  IGetCourseStatusRequest,
  IGetCourseStatusResponse,
  IGetCourseTagsResponse,
  IGetFreshStartServersForCourseRequest,
  IGetFreshStartServersForCourseResponse,
  IGetOsTemplatesOfServerRequest,
  IGetOsTemplatesOfServerResponse,
  IJoinToCourseRequest,
  ISaveIsFreshStartMachineReadyToCloneRequest,
  ISaveMachineIframeSizeRequest,
  ISaveParticipantsMachineControlActiveRequest,
  ISaveShowOnlyActiveMachinesRequest,
  ISaveWhetherTrainerAllowedPreviewRequest,
  IUpdateCourseRequest,
  Result
} from '@libs/models';

import { BaseApi } from './api-client';

export class CoursesApi extends BaseApi {
  protected readonly baseUrl = '/api/courses';

  async getCourses(
    request: IGetCoursesRequest,
    signal?: AbortSignal
  ): Promise<Result<IGetCoursesResponse>> {
    return this.post<IGetCoursesResponse>('/search', request, 'Get courses failed', signal);
  }

  async getCourse(request: IGetCourseRequest): Promise<Result<IGetCourseResponse>> {
    return this.get<IGetCourseResponse>(`/${request.courseId}`, null, 'Get course failed');
  }

  async getCourseJoinCode(
    request: IGetCourseJoinCodeRequest
  ): Promise<Result<IGetCourseJoinCodeResponse>> {
    return this.get<IGetCourseJoinCodeResponse>(
      `/${request.courseId}/join-code`,
      null,
      'Get course join code failed'
    );
  }

  async getCourseForEdit(
    request: IGetCourseForEditRequest
  ): Promise<Result<IGetCourseForEditResponse>> {
    return this.get<IGetCourseForEditResponse>(
      `/${request.courseId}/for-edit`,
      null,
      'Get course for edit failed'
    );
  }

  async joinCourse(
    request: IJoinToCourseRequest
  ): Promise<Result<IGetCourseStatusResponse | null>> {
    return this.post<IGetCourseStatusResponse | null>(
      '/joinToCourse',
      request,
      'Join course failed'
    );
  }

  async getCourseStatus(
    request: IGetCourseStatusRequest
  ): Promise<Result<IGetCourseStatusResponse>> {
    return this.get<IGetCourseStatusResponse>('/join/status', request, 'Get course status failed');
  }

  async getCourseTags(): Promise<Result<IGetCourseTagsResponse>> {
    return this.get<IGetCourseTagsResponse>('/tags', null, 'Get course tags failed');
  }

  async saveMachineIframeSize(request: ISaveMachineIframeSizeRequest): Promise<void> {
    return this.postVoid('/saveMachineIframeSize', request);
  }

  async getFreshStartServersForCourse(
    request: IGetFreshStartServersForCourseRequest
  ): Promise<Result<IGetFreshStartServersForCourseResponse>> {
    return this.get<IGetFreshStartServersForCourseResponse>(
      '/getFreshStartServersForCourse',
      request,
      'Get fresh start servers failed'
    );
  }

  async getOsTemplatesOfServer(
    request: IGetOsTemplatesOfServerRequest
  ): Promise<Result<IGetOsTemplatesOfServerResponse>> {
    return this.get<IGetOsTemplatesOfServerResponse>(
      '/getOsTemplatesOfServer',
      request,
      'Get OS templates failed'
    );
  }

  async createCourse(request: ICreateCourseRequest): Promise<Result<ICreateCourseResponse>> {
    return this.post<ICreateCourseResponse>('', request, 'Create course failed');
  }

  async updateCourse(request: IUpdateCourseRequest): Promise<void> {
    return this.putVoid(`/${request.courseId}`, request);
  }

  async saveIsFreshStartMachineReadyToClone(
    request: ISaveIsFreshStartMachineReadyToCloneRequest
  ): Promise<void> {
    return this.postVoid('/saveIsFreshStartMachineReadyToClone', request);
  }

  async saveWhetherTrainerAllowedPreview(
    request: ISaveWhetherTrainerAllowedPreviewRequest
  ): Promise<void> {
    return this.postVoid('/saveWhetherTrainerAllowedPreview', request);
  }

  async saveShowOnlyActiveMachines(request: ISaveShowOnlyActiveMachinesRequest): Promise<void> {
    return this.postVoid('/saveShowOnlyActiveMachines', request);
  }

  async saveParticipantsMachineControlActive(
    request: ISaveParticipantsMachineControlActiveRequest
  ): Promise<void> {
    return this.postVoid('/saveParticipantsMachineControlActive', request);
  }
}
